import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import axios from 'axios'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  getGuidanceSupportData,
  saveSelectedScripts,
  isProcessingResponse,
  isCompletedResponse,
} from '@/apps/legacy/features/guidance/apis/guidance'
import { GuidanceSupportData } from '@/apps/legacy/features/guidance/types/guidanceTypes'

// back V2 envelope `{status, data?}` を legacy V2 UI が期待する `{data, isGenerating}` に
// adapt する. processing → 生成中 / completed → 完成 / failed|skipped → データなし.
const adaptEnvelope = (
  responseData: unknown
): { data: GuidanceSupportData | null; isGenerating: boolean } => {
  if (isProcessingResponse(responseData)) {
    return { data: null, isGenerating: true }
  }
  if (isCompletedResponse(responseData)) {
    return {
      data: {
        scriptData: responseData.data.scriptData,
        detailData: responseData.data.detailData,
        requestId: responseData.data.requestId,
      },
      isGenerating: false,
    }
  }
  return { data: null, isGenerating: false }
}

interface UseGuidanceQueryOptions {
  autoCheck?: boolean
}

interface GuidanceQueryResult {
  data: GuidanceSupportData | null
  isLoading: boolean
  error: Error | null
  isGenerating: boolean
  generateGuidance: () => Promise<void>
}

/**
 * 指導支援データ取得クエリ.
 *
 * - autoCheck=true でマウント時に GET 1 本叩く (= back 側で row 作成 + LLM 生成 trigger 込み)
 * - 生成中 (= status='processing') は 2 秒 polling
 * - 30 分キャッシュ
 */
export const useGuidanceQuery = (
  yakurekiId: string | undefined,
  options: UseGuidanceQueryOptions = {}
): GuidanceQueryResult => {
  const { autoCheck = false } = options
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: queryKeys.guidance.detail(yakurekiId ?? ''),
    queryFn: async (): Promise<{
      data: GuidanceSupportData | null
      isGenerating: boolean
    }> => {
      if (!yakurekiId) {
        return { data: null, isGenerating: false }
      }
      const response = await getGuidanceSupportData(yakurekiId)
      return adaptEnvelope(response.data)
    },
    enabled: !!yakurekiId && autoCheck,
    staleTime: 1000 * 60 * 30,
    retry: (failureCount, error) => {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return false
      }
      // 409 mismatch (= V3 で生成済を V2 user が開いたケース) は retry しない
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        return false
      }
      return failureCount < 3
    },
  })

  const isGenerating = query.data?.isGenerating ?? false

  useQuery({
    queryKey: [...queryKeys.guidance.detail(yakurekiId ?? ''), 'polling'],
    queryFn: async () => {
      if (!yakurekiId) return null
      try {
        const response = await getGuidanceSupportData(yakurekiId)
        const result = adaptEnvelope(response.data)
        if (!result.isGenerating) {
          queryClient.setQueryData(
            queryKeys.guidance.detail(yakurekiId),
            result
          )
        }
        return { isGenerating: result.isGenerating }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 403) {
          queryClient.setQueryData(queryKeys.guidance.detail(yakurekiId), {
            data: null,
            isGenerating: false,
          })
          return { isGenerating: false }
        }
        throw error
      }
    },
    enabled: !!yakurekiId && isGenerating,
    refetchInterval: isGenerating ? 2000 : false,
    refetchIntervalInBackground: false,
    retry: (failureCount) => failureCount < 3,
  })

  // 患者切り替え時にキャッシュをクリア
  useEffect(() => {
    return () => {
      if (yakurekiId) {
        queryClient.removeQueries({
          queryKey: queryKeys.guidance.detail(yakurekiId),
        })
      }
    }
  }, [yakurekiId, queryClient])

  const generateMutation = useMutation({
    mutationFn: async (id: string) => {
      // fire-and-forget: GET で生成 trigger.
      getGuidanceSupportData(id).catch(() => {})
      return id
    },
    onSuccess: (_result, id) => {
      // 楽観的に isGenerating=true をセット → polling 開始.
      queryClient.setQueryData(queryKeys.guidance.detail(id), {
        data: null,
        isGenerating: true,
      })
    },
  })

  const generateGuidance = async (): Promise<void> => {
    if (!yakurekiId) return
    await generateMutation.mutateAsync(yakurekiId)
  }

  return {
    data: query.data?.data ?? null,
    isLoading: query.isLoading || generateMutation.isPending || isGenerating,
    error: query.error instanceof Error ? query.error : null,
    isGenerating,
    generateGuidance,
  }
}

export const useSaveGuidanceScriptsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      yakurekiId,
      scriptSelections,
      scriptOrder,
    }: {
      yakurekiId: string
      scriptSelections: Record<string, boolean>
      scriptOrder: string[]
    }) => {
      const response = await saveSelectedScripts(
        yakurekiId,
        scriptSelections,
        scriptOrder
      )
      return response.data
    },
    onSuccess: (_data, { yakurekiId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.guidance.detail(yakurekiId),
      })
    },
  })
}
